import React, { useCallback, useEffect, useRef, useState } from 'react'

export type CameraPreviewStatus = 'idle' | 'requesting' | 'streaming' | 'denied' | 'unavailable'

export interface UseCameraPreviewReturn {
  videoRef: React.RefObject<HTMLVideoElement | null>
  status: CameraPreviewStatus
}

/**
 * Live camera viewfinder used purely as an affordance: the frame is never
 * captured from the stream — tapping the tile opens the native camera instead,
 * so the photo keeps its original quality.
 *
 * Permission is asked at most once per mount: a refusal is final until the user
 * changes it in the browser settings, so retrying would only nag.
 */
const useCameraPreview = (enabled: boolean): UseCameraPreviewReturn => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [status, setStatus] = useState<CameraPreviewStatus>('idle')
  // Mirror of the status for the visibilitychange listener, which must not
  // resubscribe on every status change.
  const statusRef = useRef<CameraPreviewStatus>('idle')
  // Generation counter: a stream granted after unmount or after the tile was
  // disabled belongs to a dead run and has to be released, not attached.
  const runIdRef = useRef<number>(0)

  const applyStatus = useCallback((next: CameraPreviewStatus) => {
    statusRef.current = next
    setStatus(next)
  }, [])

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  const request = useCallback(async () => {
    const media = navigator.mediaDevices
    // No mediaDevices at all: insecure context (plain http) or an old browser.
    if (!media?.getUserMedia) {
      applyStatus('unavailable')
      return
    }
    const runId = ++runIdRef.current
    applyStatus('requesting')
    try {
      const stream = await media.getUserMedia({ video: { facingMode: 'environment' } })
      if (runIdRef.current !== runId) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play?.()?.catch(() => {})
      }
      applyStatus('streaming')
    } catch {
      if (runIdRef.current !== runId) return
      applyStatus('denied')
    }
  }, [applyStatus])

  useEffect(() => {
    if (!enabled) {
      runIdRef.current += 1
      stopStream()
      applyStatus('idle')
      return
    }
    request()
    return () => {
      runIdRef.current += 1
      stopStream()
    }
  }, [enabled, request, stopStream, applyStatus])

  useEffect(() => {
    if (!enabled) return
    /**
     * The native camera takes the device over, so the preview track comes back
     * ended. Re-acquire on return to the tab — but only then: a still-live track
     * needs nothing, and a refused permission must not be asked again.
     */
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return
      if (statusRef.current !== 'streaming') return
      const isBroken = streamRef.current?.getTracks().some((t) => t.readyState === 'ended') ?? false
      if (!isBroken) return
      stopStream()
      request()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [enabled, request, stopStream])

  return { videoRef, status }
}

export default useCameraPreview
